import { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { CountryKeySelect } from '@app/form';
import {
  QuoteInformation,
  StudyType,
  useBillingInformationsByOrga,
  useOffers,
  useOrganizationCapabilities,
} from 'queries';
import { OfferList, OfferOptionBlock } from 'components';
import { SetupSection } from 'pages/StudySetup/components/SetupSection';
import { requirePriceTier } from 'pages/StudySetup/GeneralSetup/filterOffers';
import { BillingMethodSection } from 'pages/StudySetup/GeneralSetup/Sections/BillingMethodSection';
import { SupportSection } from 'pages/StudySetup/GeneralSetup/Sections/SupportSection';
import { StudySubjectSection } from 'pages/StudySetup/GeneralSetup/Sections/StudySubjectSection';
import { StudyTypeSection } from 'pages/StudySetup/GeneralSetup/Sections/StudyTypeSection';
import { GroupMeetingSection } from 'pages/StudySetup/GeneralSetup/Sections/GroupMeetingSection';
import { StudyFormatModeratedSection } from 'pages/StudySetup/GeneralSetup/Sections/StudyFormatModeratedSection';
import { StudyFormatUnmoderatedSection } from 'pages/StudySetup/GeneralSetup/Sections/StudyFormatUnmoderatedSection';
import { DurationSection } from 'pages/StudySetup/GeneralSetup/Sections/DurationSection';
import { NumberOfParticipantsSection } from 'pages/StudySetup/GeneralSetup/Sections/NumberOfParticipantsSection';
import { useQuoteOffers } from './useQuoteOffers';

interface Props {
  visible: boolean;
  organizationId?: string;
}

export const QuoteStudySetup = ({ visible, organizationId }: Props) => {
  const { t } = useTranslation();
  const { values, setFieldValue } =
    useFormikContext<Partial<QuoteInformation>>();
  const { data: offers } = useOffers();

  const { data: recipientCapabilities } =
    useOrganizationCapabilities(organizationId);
  const { data: recipientBillingInformations } =
    useBillingInformationsByOrga(organizationId);

  const {
    availableOffers,
    targetingOffers,
    optionOffers,
    supportOffers,
    selectedOffers,
  } = useQuoteOffers(values, recipientCapabilities);

  const showPriceTier = useMemo(
    () => requirePriceTier(values, offers),
    [values, offers],
  );

  const offerRequireCallToSales = selectedOffers.some(
    (offer) => !!offer.requireCallToSales,
  );

  const inheritedBillingMethod =
    recipientBillingInformations?.defaultStudyBillingMethod;

  useEffect(() => {
    setFieldValue('billingMethod', inheritedBillingMethod);
  }, [organizationId, inheritedBillingMethod, setFieldValue]);

  useEffect(() => {
    const offerIds = availableOffers.map(({ id }) => id);

    if (
      values.targetingOfferId &&
      !offerIds.includes(values.targetingOfferId)
    ) {
      setFieldValue('targetingOfferId', undefined);
    }
    if (values.supportOfferId && !offerIds.includes(values.supportOfferId)) {
      setFieldValue('supportOfferId', undefined);
    }
    if (values.priceThierId && !showPriceTier) {
      setFieldValue('priceThierId', undefined);
    }
  }, [values, availableOffers, showPriceTier, setFieldValue]);

  const requiresFormat =
    values.studyType === StudyType.MODERATED ||
    values.studyType === StudyType.UNMODERATED;

  const willSelectOffer =
    visible &&
    !!values.studySubject &&
    !!values.studyType &&
    (!requiresFormat || !!values.format);

  return (
    <>
      <SetupSection
        title={t('studySetup.generalSetup.studySubject.title')}
        visible={visible}
        trackId="studySubject"
      >
        <StudySubjectSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.studyType.title')}
        visible={visible && !!values.studySubject}
        trackId="studyType"
      >
        <StudyTypeSection hideDiary />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.groupMeeting.title')}
        visible={visible && values.studyType === StudyType.MODERATED}
        trackId="studyGroupMeeting"
      >
        <GroupMeetingSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.format.title')}
        visible={
          visible &&
          values.studyType === StudyType.MODERATED &&
          values.groupMeeting !== undefined
        }
        trackId="studyFormat"
      >
        <StudyFormatModeratedSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.format.titleUnmoderated')}
        visible={visible && values.studyType === StudyType.UNMODERATED}
        trackId="studyFormat"
      >
        <StudyFormatUnmoderatedSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.duration.title')}
        visible={
          willSelectOffer && (targetingOffers.length > 0 || showPriceTier)
        }
        trackId="duration"
      >
        <DurationSection />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.priceTier.title')}
        visible={willSelectOffer && !!values.duration}
        trackId="priceTiers"
      >
        <NumberOfParticipantsSection showQuantityPriceTier={showPriceTier} />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.countryKeys.title')}
        visible={
          willSelectOffer &&
          !!targetingOffers.length &&
          !!values.duration &&
          !!values.numberOfParticipantRequired
        }
        trackId="countryKeys"
      >
        <CountryKeySelect isMulti name="countryKeys" />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.offers.title')}
        visible={
          willSelectOffer &&
          (!showPriceTier || !!values.priceThierId) &&
          !!targetingOffers.length &&
          !!values.duration &&
          !!values.countryKeys?.length
        }
        trackId="targetingOffers"
      >
        <OfferList
          offers={targetingOffers}
          name="targetingOfferId"
          duration={values.duration}
          countryKeys={values.countryKeys}
        />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.support.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          !!supportOffers.length &&
          (!offerRequireCallToSales || !!values.supportOfferId)
        }
        trackId="supportOffers"
      >
        <SupportSection
          offers={supportOffers}
          selectedOffers={selectedOffers}
        />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.option.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          (!!values.supportOfferId || !supportOffers.length) &&
          (!offerRequireCallToSales || !!values.optionsOfferIds?.length) &&
          !!optionOffers.length
        }
        trackId="options"
      >
        <OfferOptionBlock offers={optionOffers} />
      </SetupSection>
      <SetupSection
        title={t('studySetup.generalSetup.billingMethod.title')}
        visible={
          willSelectOffer &&
          !!values.targetingOfferId &&
          (!!values.supportOfferId || !supportOffers.length) &&
          !offerRequireCallToSales &&
          (!!values.optionsOfferIds || !optionOffers.length) &&
          !inheritedBillingMethod
        }
        trackId="clientBillingMethod"
      >
        <BillingMethodSection showBillingCountry={!organizationId} />
      </SetupSection>
    </>
  );
};
